import { ReactNode } from "react";
import clsx from "clsx";

interface CardProps {
  title: string;
  description?: string;
  action?: ReactNode;
  children: ReactNode;
  className?: string;
}

export function Card({
  title,
  description,
  action,
  children,
  className,
}: CardProps) {
  return (
    <section
      className={clsx(
        "rounded-md border border-gray-200 bg-white p-4 shadow-sm",
        className,
      )}
    >
      <div className="mb-3 flex items-start justify-between gap-3">
        <div>
          <h2 className="text-sm font-semibold uppercase tracking-wide text-gray-600">
            {title}
          </h2>
          {description ? (
            <p className="mt-0.5 text-xs text-gray-500">{description}</p>
          ) : null}
        </div>
        {action}
      </div>
      {children}
    </section>
  );
}

interface StatCardProps {
  label: string;
  value: ReactNode;
  hint?: string;
  accentClassName?: string;
}

export function StatCard({
  label,
  value,
  hint,
  accentClassName = "border-l-orange-500",
}: StatCardProps) {
  return (
    <div
      className={clsx(
        "flex min-w-[9rem] flex-1 flex-col rounded-md border border-l-4 border-gray-200 bg-white px-3 py-2",
        accentClassName,
      )}
    >
      <span className="text-xs text-gray-500">{label}</span>
      <span className="text-2xl font-semibold text-slate-800">{value}</span>
      {hint ? <span className="text-xs text-gray-500">{hint}</span> : null}
    </div>
  );
}

interface BarRowProps {
  label: ReactNode;
  value: number;
  max: number;
  barClassName: string;
  valueLabel?: ReactNode;
  onClick?: () => void;
}

/** Horizontal bar with the label on the left and the number on the right. */
export function BarRow({
  label,
  value,
  max,
  barClassName,
  valueLabel,
  onClick,
}: BarRowProps) {
  const width = max > 0 ? Math.max(value > 0 ? 2 : 0, (value / max) * 100) : 0;
  const Wrapper = onClick ? "button" : "div";

  return (
    <Wrapper
      type={onClick ? "button" : undefined}
      onClick={onClick}
      className={clsx(
        "flex w-full items-center gap-3 text-left",
        onClick && "rounded hover:bg-gray-50",
      )}
    >
      <div className="w-36 shrink-0 truncate text-sm text-slate-700">
        {label}
      </div>
      <div className="h-2.5 flex-1 overflow-hidden rounded bg-gray-100">
        <div
          className={clsx("h-full rounded", barClassName)}
          style={{ width: `${width}%` }}
        />
      </div>
      <div className="w-20 shrink-0 text-right text-sm tabular-nums text-slate-800">
        {valueLabel ?? value}
      </div>
    </Wrapper>
  );
}

export interface StackedSegment {
  key: string;
  value: number;
  className: string;
  title?: string;
}

/** One bar split into coloured segments, proportional to each segment's share. */
export function StackedBar({
  segments,
  className,
}: {
  segments: StackedSegment[];
  className?: string;
}) {
  const total = segments.reduce((sum, s) => sum + s.value, 0);
  if (total === 0) {
    return (
      <div className={clsx("h-2.5 w-full rounded bg-gray-100", className)} />
    );
  }

  return (
    <div
      className={clsx(
        "flex h-2.5 w-full overflow-hidden rounded bg-gray-100",
        className,
      )}
    >
      {segments
        .filter((s) => s.value > 0)
        .map((s) => (
          <div
            key={s.key}
            className={s.className}
            style={{ width: `${(s.value / total) * 100}%` }}
            title={s.title ?? `${s.key}: ${s.value}`}
          />
        ))}
    </div>
  );
}

/** Period-over-period arrow. Hidden when there is no previous period to compare to. */
export function TrendBadge({
  current,
  previous,
}: {
  current: number;
  previous?: number;
}) {
  if (previous === undefined || previous === null) return null;

  if (previous === 0 && current > 0) {
    return (
      <span
        className="text-xs font-medium text-orange-600"
        title="Did not appear in the previous period"
      >
        new
      </span>
    );
  }

  const delta = current - previous;
  if (delta === 0) {
    return <span className="text-xs text-gray-400">→ 0</span>;
  }

  const pct = previous === 0 ? 100 : Math.round((Math.abs(delta) / previous) * 100);

  return (
    <span
      className={clsx(
        "text-xs font-medium tabular-nums",
        delta > 0 ? "text-rose-600" : "text-green-600",
      )}
      title={`${previous} in the previous period`}
    >
      {delta > 0 ? "↑" : "↓"} {Math.abs(delta)} ({pct}%)
    </span>
  );
}

export function EmptyState({ children }: { children: ReactNode }) {
  return (
    <p className="rounded border border-dashed border-gray-300 px-3 py-6 text-center text-sm text-gray-500">
      {children}
    </p>
  );
}
